<script lang="ts">
  import type { CarouselInstanceRef, CarouselSlide } from './Carousel.types';

  export * from './Carousel.types';
</script>

<script setup lang="ts" generic="Slide extends CarouselSlide">
  import 'vue3-carousel/dist/carousel.css';
  import { ref, useAttrs, useCssModule } from 'vue';
  import * as Vue3Carousel from 'vue3-carousel';

  import { t } from '../../locale';
  import Thumbnail from '../Thumbnail/Thumbnail.vue';
  import ThumbnailGroup from '../ThumbnailGroup/ThumbnailGroup.vue';

  defineOptions({
    inheritAttrs: false,
  });

  const props = withDefaults(
    defineProps<{
      /**
       * The slides to display in the carousel.
       */
      slides: Slide[];

      /**
       * Time, in ms, before slides will automatically transition.
       * If not specified, slides are static until manually transitioned.
       */
      autoplayInterval?: number;

      /**
       * Hide the navigation buttons
       */
      hideNavigation?: boolean;

      /**
       * Hide the pagination dots/tile
       */
      hidePagination?: boolean;

      /**
       * Number of slides to be scrolled at a time
       */
      itemsToScroll?: number;

      /**
       * Number of items to show on each slide
       */
      itemsToShow?: number;

      /**
       * Whether the carousel should loop back to the first slide after the last slide.
       */
      loop?: boolean;

      /**
       * The type of pagination to display.
       */
      pagination?: 'tile' | 'dot';

      /**
       * The radius of the pagination tiles.
       */
      paginationTileRadius?: 'standard' | 'full';

      /**
       * Prevents slides from automatically transitioning when hovering over the slide.
       */
      pauseAutoplayOnHover?: boolean;

      /**
       * Whether to round the borders of the slides.
       */
      roundBorders?: boolean;

      /**
       * Whether to show the pagination dots on all screen sizes.
       */
      staticPaginationDots?: boolean;
    }>(),
    {
      autoplayInterval: 0,
      hideNavigation: false,
      hidePagination: false,
      itemsToScroll: 1,
      itemsToShow: 1,
      loop: false,
      pagination: 'tile',
      paginationTileRadius: 'standard',
      pauseAutoplayOnHover: false,
      roundBorders: false,
      staticPaginationDots: false,
    },
  );

  const attrs = useAttrs();
  const classes = useCssModule();
  const carouselRef = ref<CarouselInstanceRef>();

  const emit = defineEmits<{
    (e: 'click', slide: Slide): void;
    (e: 'transition', slide: Slide): void;
    (e: 'transition:multi', slides: Slide[]): void;
  }>();

  const currentIndex = ref(0);

  function handleUpdateCarouselModelValue(...args: unknown[]) {
    const [index] = args;
    if (index === undefined) return;
    slideTo(index as number);
  }

  const slideTo = (index: number) => {
    currentIndex.value = index;
  };

  const onCarouselInit = () => {
    // carousel does not fire a transition event to the first slide when mounted.
    // subsequent transition events are fired in the slide-end event handler.
    onSlideTransition();
  };

  const onSlideClick = () => {
    emit('click', props.slides[currentIndex.value]);
  };

  const onSlideTransition = () => {
    // by default only one slide is displayed at a time for each
    // page in the carousel.  In this case, we will only emit a
    // transition event with a single slide object.
    if (props.itemsToShow === 1) {
      emit('transition', props.slides[currentIndex.value]);
      return;
    }

    // otherwise, we will emit a transition event with an array of
    // slides currently in view on the current page of the carousel.
    const slidesInView = props.slides.slice(currentIndex.value, currentIndex.value + props.itemsToShow);

    emit('transition:multi', slidesInView);
    return;
  };

  /**
   * Expose the carousel's next and prev and a move slide methods to the parent component
   */
  defineExpose({
    next: () => carouselRef.value?.next(),
    prev: () => carouselRef.value?.prev(),
    slideTo,
  });
</script>

<template>
  <Vue3Carousel.Carousel
    ref="carouselRef"
    :model-value="currentIndex"
    aria-live="polite"
    aria-roledescription="carousel"
    class="stash-carousel mb-6"
    data-test="stash-carousel"
    :autoplay="autoplayInterval"
    :class="classes.root"
    :i18n="{
      ariaNextSlide: t('ll.next'),
      ariaPreviousSlide: t('ll.previous'),
      iconArrowLeft: t('ll.previous'),
      iconArrowRight: t('ll.next'),
    }"
    :items-to-scroll="props.itemsToScroll"
    :items-to-show="props.itemsToShow"
    :pause-autoplay-on-hover="pauseAutoplayOnHover"
    :wrap-around="props.loop"
    v-bind="attrs"
    @init="onCarouselInit"
    @slide-end="onSlideTransition"
    @update:model-value="handleUpdateCarouselModelValue"
  >
    <Vue3Carousel.Slide
      v-for="(slide, index) in props.slides"
      :key="slide.id"
      data-test="stash-carousel|slide"
      class="cursor-pointer"
      @click="onSlideClick"
    >
      <slot name="slide" :slide="slide" :index="index">
        <img
          class="size-full"
          :class="[{ rounded: props.roundBorders }]"
          :src="slide.imageUrl"
          :title="
            t('ll.carousel.imageLabel', {
              index: index + 1,
              total: slides.length,
            })
          "
        />
      </slot>
    </Vue3Carousel.Slide>

    <template #addons>
      <Vue3Carousel.Navigation v-if="props.slides.length > 1 && !props.hideNavigation" />
    </template>
  </Vue3Carousel.Carousel>

  <template v-if="!props.hidePagination">
    <!-- Pagination Tiles -->
    <ThumbnailGroup
      v-if="props.pagination === 'tile'"
      :active-thumbnail="currentIndex"
      :thumbnails="slides"
      :radius="props.paginationTileRadius === 'full' ? 'circle' : 'rounded'"
      @update:active-thumbnail="slideTo"
    >
      <Thumbnail v-for="slide in slides" :key="slide.id" :thumbnail="slide" variant="translucent" />
    </ThumbnailGroup>

    <!-- Pagination Dots -->
    <ul
      v-else
      class="text-center"
      data-test="stash-carousel|pagination-dots"
      :class="[!staticPaginationDots && 'lg:hidden']"
    >
      <li
        v-for="(slide, index) in slides"
        :key="slide.imageUrl"
        class="inline-block rounded-full"
        :class="[classes['pagination-dot'], [currentIndex === index ? 'bg-blue-500' : 'bg-blue-100']]"
      />
    </ul>
  </template>
</template>

<style module>
  @reference "../../../styles/main.css";

  .root :global(.carousel__icon) {
    background-color: var(--color-white);
    border-radius: var(--radius-sm);
    color: var(--color-blue-500);
    height: --spacing(9);
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: --spacing(9);
  }

  .root :global(.carousel__icon:hover) {
    background-color: var(--color-ice-100);
    color: var(--color-blue-700);
  }

  .root :global(.carousel__next) {
    padding-right: --spacing(9);
  }

  .root :global(.carousel__next--disabled) {
    display: none;
  }

  .root :global(.carousel__prev) {
    padding-left: --spacing(9);
  }

  .root :global(.carousel__prev--disabled) {
    display: none;
  }

  .pagination-dot {
    height: 4px;
    margin-right: 2px;
    width: 4px;
  }
</style>
